import type { WebChatMessage } from "./Chat"

export type MockTransportMode = "sse" | "polling"

interface ScheduledFrame {
  frame: WebChatMessage
  delayMs: number
}

const TOOL_SCENARIOS: Array<{ toolName: string; toolLabel: string; doneLabel: string; durationMs: number }> = [
  { toolName: "searchChannels", toolLabel: "Searching emails...", doneLabel: "Searched emails", durationMs: 2100 },
  { toolName: "getAvailableSlots", toolLabel: "Checking calendar...", doneLabel: "Checked calendar", durationMs: 1800 },
  { toolName: "honchoAsk", toolLabel: "Checking memory...", doneLabel: "Checked memory", durationMs: 1200 },
]

const MOCK_RESPONSES = [
  "Based on your recent emails, you have a meeting with Sarah Chen on Thursday at 2pm to discuss the partnership terms. I also found three unread messages from opposing counsel regarding the NDA revision.",
  "I found the relevant threads. The latest update from legal is that the indemnification clause needs revision before the Friday deadline. Want me to pull up the specific language they flagged?",
  "Here's what I found: your calendar shows two available slots tomorrow — 10am and 3pm. The email thread with David suggests he prefers afternoon meetings. Should I propose the 3pm slot?",
]

export class MockChatController {
  private frameCallback: ((frame: WebChatMessage) => void) | null = null
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = []
  private frameBuffer: Map<string, WebChatMessage[]> = new Map()
  private responseIndex = 0

  setFrameCallback(cb: (frame: WebChatMessage) => void): void {
    this.frameCallback = cb
  }

  clearFrameCallback(): void {
    this.frameCallback = null
  }

  simulateResponse(chatSessionId: string): void {
    const now = Date.now()
    const responseText = MOCK_RESPONSES[this.responseIndex % MOCK_RESPONSES.length]
    this.responseIndex++

    const schedule: ScheduledFrame[] = [
      {
        delayMs: 0,
        frame: this.buildFrame(chatSessionId, "typing", now, {
          thinkingState: { phase: "reasoning", label: "Thinking..." },
        }),
      },
      {
        delayMs: 1500,
        frame: this.buildFrame(chatSessionId, "tool_start", now + 1500, {
          toolProgress: { toolName: TOOL_SCENARIOS[0].toolName, toolLabel: TOOL_SCENARIOS[0].toolLabel },
        }),
      },
      {
        delayMs: 1500 + TOOL_SCENARIOS[0].durationMs,
        frame: this.buildFrame(chatSessionId, "tool_done", now + 1500 + TOOL_SCENARIOS[0].durationMs, {
          toolProgress: { toolName: TOOL_SCENARIOS[0].toolName, toolLabel: TOOL_SCENARIOS[0].doneLabel, durationMs: TOOL_SCENARIOS[0].durationMs },
        }),
      },
      {
        delayMs: 4000,
        frame: this.buildFrame(chatSessionId, "tool_start", now + 4000, {
          toolProgress: { toolName: TOOL_SCENARIOS[1].toolName, toolLabel: TOOL_SCENARIOS[1].toolLabel },
        }),
      },
      {
        delayMs: 4000 + TOOL_SCENARIOS[1].durationMs,
        frame: this.buildFrame(chatSessionId, "tool_done", now + 4000 + TOOL_SCENARIOS[1].durationMs, {
          toolProgress: { toolName: TOOL_SCENARIOS[1].toolName, toolLabel: TOOL_SCENARIOS[1].doneLabel, durationMs: TOOL_SCENARIOS[1].durationMs },
        }),
      },
      {
        delayMs: 6200,
        frame: this.buildFrame(chatSessionId, "tool_start", now + 6200, {
          toolProgress: { toolName: TOOL_SCENARIOS[2].toolName, toolLabel: TOOL_SCENARIOS[2].toolLabel },
        }),
      },
      {
        delayMs: 6200 + TOOL_SCENARIOS[2].durationMs,
        frame: this.buildFrame(chatSessionId, "tool_done", now + 6200 + TOOL_SCENARIOS[2].durationMs, {
          toolProgress: { toolName: TOOL_SCENARIOS[2].toolName, toolLabel: TOOL_SCENARIOS[2].doneLabel, durationMs: TOOL_SCENARIOS[2].durationMs },
        }),
      },
      {
        delayMs: 8500,
        frame: this.buildFrame(chatSessionId, "message", now + 8500, {
          bodyText: responseText,
        }),
      },
    ]

    for (const { frame, delayMs } of schedule) {
      const timeout = setTimeout(() => {
        this.emitFrame(frame)
      }, delayMs)
      this.pendingTimeouts.push(timeout)
    }
  }

  pollFrames(chatSessionId: string, afterIso?: string): WebChatMessage[] {
    const buffer = this.frameBuffer.get(chatSessionId) ?? []
    if (!afterIso) {
      this.frameBuffer.set(chatSessionId, [])
      return buffer
    }
    const cutoff = new Date(afterIso).getTime()
    const result = buffer.filter((f) => new Date(f.createdAt).getTime() > cutoff)
    this.frameBuffer.set(chatSessionId, buffer.filter((f) => new Date(f.createdAt).getTime() <= cutoff))
    return result
  }

  dispose(): void {
    for (const t of this.pendingTimeouts) clearTimeout(t)
    this.pendingTimeouts = []
    this.frameBuffer.clear()
    this.frameCallback = null
  }

  private emitFrame(frame: WebChatMessage): void {
    if (this.frameCallback) {
      this.frameCallback(frame)
    }
    const buffer = this.frameBuffer.get(frame.chatSessionId) ?? []
    buffer.push(frame)
    this.frameBuffer.set(frame.chatSessionId, buffer)
  }

  private buildFrame(
    chatSessionId: string,
    kind: WebChatMessage["kind"],
    timestampMs: number,
    extras: { bodyText?: string; toolProgress?: any; thinkingState?: any } = {},
  ): WebChatMessage {
    return {
      webChatMessageId: crypto.randomUUID(),
      chatSessionId,
      direction: "outbound",
      kind,
      bodyText: extras.bodyText ?? null,
      toolEvents: extras.toolProgress ?? extras.thinkingState ?? null,
      createdAt: new Date(timestampMs).toISOString(),
    }
  }
}
